import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, first } from 'rxjs';
import { NotificationModel, NotificationsService, NotificationTypes } from '../../../shared/services/notifications/notifications.service';
import { SupplierCustomerStatus, SupplierCustomerStatusLabels } from '../enums/supplier-customer-status.enum';
import { Customer } from '../interfaces/customer.interface';
import { CustomerService } from '../services/customer.service';
import { AteModalService } from '@shared/services/ate-modal/ate-modal.service';

@Component({
  selector: 'customer-tile-component',
  templateUrl: './customer-tile.control.html',
  styleUrls: ['./customer-tile.control.scss'],
  standalone: false
})
export class CustomerTileComponent implements OnInit {
  @Output() public update: EventEmitter<void> = new EventEmitter<void>();
  @Input() customer: Customer;

  isToggleStatusLoading = false;
  isSetArchiveLoading = false;
  status = '';
  mobileNumber = '';
  email = '';
  statusConfig: { name: string; classes: string };

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _customerService: CustomerService,
    private _notificationsService: NotificationsService,
    private _ateModalService: AteModalService
  ) {}

  ngOnInit(): void {
    this.status = this.customer?.status;
    this.mobileNumber = this.customer?.mobileNumber;
    this.email = this.customer?.email;
    this.statusConfig = this.getStatusConfig(this.customer?.status);
  }

  editCustomer(event: Event) {
    event.stopPropagation();
    this._router.navigate(['edit', this.customer.uuid], {
      relativeTo: this._activatedRoute.parent
    });
  }

  setArchiveStatus(event: Event, isArchived = false) {
    event.stopPropagation();
    this.archiveCustomer(isArchived);
  }

  openConfirmationModal(event: Event) {
    event.stopPropagation();
    const customerName = [this.customer?.firstName, this.customer?.lastName].filter(Boolean).join(' ') || 'this customer';
    const ref = this._ateModalService.open<never, boolean>(null, {
      title: `Are you sure want to archive ${customerName}?`,
      ctaText: 'Archive Customer',
      hasCta: true,
      hasCancel: true
    });
    ref.onCtaClick = () => ref.close(true);
    ref
      .afterClosed()
      .pipe(first())
      .subscribe(confirmed => {
        if (confirmed) {
          this.archiveCustomer(true);
        }
      });
  }

  private archiveCustomer(isArchived: boolean) {
    if (!this.customer?.uuid) {
      console.error(`No customer UUID provided when trying to ${isArchived ? 'archive' : 'unarchive'} customer`);
      return;
    }
    this.isSetArchiveLoading = true;

    this._customerService
      .updateCustomer(this.customer.uuid, { isArchived })
      .pipe(
        first(),
        finalize(() => (this.isSetArchiveLoading = false))
      )
      .subscribe({
        next: () => {
          this.update.emit();
          const notification: NotificationModel = {
            message: `Customer ${isArchived ? 'archived' : 'unarchived'}.`,
            action: 'Close',
            type: NotificationTypes.SUCCESS
          };
          this._notificationsService.openSnackBar(notification);
        },
        error: error => {
          console.error(error);
          const notification: NotificationModel = {
            message: error.message || `Error ${isArchived ? 'archiving' : 'unarchiving'} customer with ID ${this.customer.uuid}. Please try again.`,
            action: 'Close',
            type: NotificationTypes.ERROR
          };
          this._notificationsService.openSnackBar(notification);
        }
      });
  }

  private getStatusConfig(status: SupplierCustomerStatus) {
    const name = SupplierCustomerStatusLabels[status];
    if (!name) {
      return { name: 'Unknown', classes: 'tw-text-slate-600 tw-bg-slate-100' };
    }
    const classes = status === SupplierCustomerStatus.COMPLETED ? 'tw-text-slate-800 tw-bg-slate-200' : 'tw-text-green-800 tw-bg-green-200';
    return { name, classes };
  }
}
